import { Button, Division, Flex, Heading, Icon, Icons } from "@ir/ui";
import {
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import "./ListFilterDrawer.scss";

interface ListFilterDrawerProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  onApply: () => void;
  onClear: () => void;
  children: ReactNode;
  applyLabel?: string;
  clearLabel?: string;
}

export function ListFilterDrawer({
  open,
  title = "Filters",
  onClose,
  onApply,
  onClear,
  children,
  applyLabel = "Apply",
  clearLabel = "Clear",
}: Readonly<ListFilterDrawerProps>) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [handleKeyDown, open]);

  if (!open || typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <Division className="list-filter-drawer" role="presentation">
      <Division
        className="list-filter-drawer__backdrop"
        onClick={onClose}
        aria-hidden
      />
      <Division
        className="list-filter-drawer__panel"
        role="dialog"
        aria-modal={true}
        aria-label={title}
      >
        <Flex
          align="center"
          justifyContent="space-between"
          className="list-filter-drawer__header"
        >
          <Heading level={6} className="list-filter-drawer__title m-0">
            {title}
          </Heading>
          <Button
            size="small"
            variant="clear"
            onPress={onClose}
            aria-label="Close filters"
            className="list-filter-drawer__close"
          >
            <Icon name={Icons.Close} />
          </Button>
        </Flex>

        <Division className="list-filter-drawer__body">{children}</Division>

        <Flex
          align="center"
          justifyContent="space-between"
          gap={0.5}
          className="list-filter-drawer__footer"
        >
          <Button size="small" variant="outline" onPress={onClear}>
            {clearLabel}
          </Button>
          <Button size="small" color="primary" onPress={onApply}>
            {applyLabel}
          </Button>
        </Flex>
      </Division>
    </Division>,
    document.body,
  );
}
